import { Injectable } from '@angular/core';
import { HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, interval } from 'rxjs';
import { catchError, map, switchMap, startWith } from 'rxjs/operators';
import { IngestedOrdersMetrics } from '@model/ingested-order/ingested-order.model';
import {
  OrderStatus,
  OrdersMetricsBackendResponse
} from '@infra-services/ingested-orders/api/ingested-orders-api.interface';
import { IngestedOrdersMetricsMapper } from '@infra-services/ingested-orders/mappers/ingested-orders-metrics.mapper';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';

/**
 * Service for managing order metrics.
 * Supports auto-refresh and multiple status types (ADMITTED/RECEIVED).
 */
@Injectable({
  providedIn: 'root'
})
export class OrderMetricsService {
  private readonly baseUrl: string;

  constructor(
    private readonly httpClient: AppHttpClientApi,
    private readonly errorHandler: HttpErrorHandlerService
  ) {
    this.baseUrl = `metrics/orders`;
  }

  /**
   * Fetches order metrics for the specified status.
   * @param status - ADMITTED or RECEIVED (default: ADMITTED)
   */
  getMetrics(
    status: OrderStatus = OrderStatus.INGRESADA
  ): Observable<IngestedOrdersMetrics> {
    const params = new HttpParams().set('status', status);

    return this.httpClient
      .getRaw<OrdersMetricsBackendResponse>(this.baseUrl, { params })
      .pipe(
        map((response) => IngestedOrdersMetricsMapper.toDomain(response)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getMetrics', error)
        )
      );
  }

  /**
   * Auto-refreshing stream of order metrics.
   * @param status - ADMITTED or RECEIVED (default: ADMITTED)
   * @param intervalMs - Refresh interval in milliseconds (default: 60000)
   * @example
   * this.metricsService.getMetricsStream(OrderStatus.INGRESADA, 30000)
   *   .subscribe(metrics => this.dashboardMetrics = metrics);
   */
  getMetricsStream(
    status: OrderStatus = OrderStatus.INGRESADA,
    intervalMs = 60000
  ): Observable<IngestedOrdersMetrics> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getMetrics(status))
    );
  }
}
